import { Link } from "react-router-dom";
import type { Work } from "@/mocks/works";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import type { NewsArticle } from "@/mocks/news";
import { formatViews, workFormat } from "@/mocks";

interface Props {
  latest: Work[];
  popular: Work[];
  news: NewsArticle[];
}

export default function HomeSections({ latest, popular, news }: Props) {
  const featuredNews = news[0];
  const sideNews = news.slice(1, 6);

  return (
    <>
      {/* 分类快捷入口 */}
      <section className="w-full bg-background-50 py-8 md:py-10">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/category?type=movie"
              className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className="ri-film-line text-2xl leading-none" />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-sm font-semibold text-foreground-950">电影专区</span>
                <span className="mt-1 block text-xs text-foreground-600">剧情长片 · 精选佳作</span>
              </span>
              <span className="ml-auto flex h-6 w-6 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-up-line text-lg leading-none" />
              </span>
            </Link>
            <Link
              to="/category?type=tv"
              className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-accent-300"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
                <i className="ri-tv-2-line text-2xl leading-none" />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-sm font-semibold text-foreground-950">电视剧专区</span>
                <span className="mt-1 block text-xs text-foreground-600">连续剧集 · 分集剧情</span>
              </span>
              <span className="ml-auto flex h-6 w-6 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-up-line text-lg leading-none" />
              </span>
            </Link>
            <Link
              to="/news"
              className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-secondary-300"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 transition-colors group-hover:bg-secondary-500 group-hover:text-background-50">
                <i className="ri-newspaper-line text-2xl leading-none" />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-sm font-semibold text-foreground-950">影视资讯</span>
                <span className="mt-1 block text-xs text-foreground-600">定档动态 · 幕后解读</span>
              </span>
              <span className="ml-auto flex h-6 w-6 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-up-line text-lg leading-none" />
              </span>
            </Link>
            <Link
              to="/category"
              className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200/80 bg-background-100 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-grid-line text-2xl leading-none" />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-sm font-semibold text-foreground-950">全集筛选</span>
                <span className="mt-1 block text-xs text-foreground-600">类型地区 · 年份排序</span>
              </span>
              <span className="ml-auto flex h-6 w-6 items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-up-line text-lg leading-none" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 最新影视 + 热门推荐 */}
      <section className="w-full bg-background-50 py-4 md:py-8">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-600">Latest</p>
                <h2 className="mt-1.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">最新影视作品</h2>
              </div>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-600 transition-colors hover:text-primary-700 md:text-sm"
              >
                查看全部
                <i className="ri-arrow-right-line text-sm leading-none" />
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
              {latest.slice(0, 6).map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-foreground-950">热门影视推荐</h2>
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-900">
                  <i className="ri-fire-line text-[11px] leading-none" />
                  浏览量
                </span>
              </div>
              <ul className="mt-2">
                {popular.map((work, i) => (
                  <li key={work.id}>
                    <Link
                      to={`/detail/${work.id}`}
                      className="group flex cursor-pointer items-center gap-3 border-b border-background-200/70 py-3 last:border-b-0"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-bold ${
                          i < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={work.poster}
                          alt={`${work.title} 海报`}
                          title={`${work.title} 剧情介绍`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                          {work.title}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-500">
                          <span className="whitespace-nowrap">{workFormat(work.type)}</span>
                          <span className="text-background-400">·</span>
                          <span className="whitespace-nowrap">{work.genres[0]}</span>
                          <span className="text-background-400">·</span>
                          <span className="whitespace-nowrap">{formatViews(work.views)} 次浏览</span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg bg-primary-500 p-5 text-background-50">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center">
                  <i className="ri-quill-pen-line text-lg leading-none" />
                </span>
                <h2 className="font-heading text-base font-bold">剧情研究专题</h2>
              </div>
              <p className="mt-2.5 text-xs leading-relaxed text-background-100/85">
                从叙事结构到镜头语言，逐部拆解「女友的妈妈」相关影视作品背后的创作逻辑，帮你看懂每一个细节。
              </p>
              <Link
                to="/news"
                className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-3.5 py-2 text-xs font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                进入专题阅读
                <i className="ri-arrow-right-line text-sm leading-none" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 影视资讯精选 */}
      <section className="w-full border-y border-background-200/70 bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-600">News</p>
              <h2 className="mt-1.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">影视资讯精选</h2>
              <p className="mt-2 text-sm text-foreground-600">定档动态、幕后特辑与深度解读，一站掌握行业最新动向。</p>
            </div>
            <Link
              to="/news"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 md:text-sm"
            >
              全部资讯
              <i className="ri-arrow-right-line text-sm leading-none" />
            </Link>
          </div>

          {featuredNews ? (
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
              <div className="lg:col-span-7">
                <Link
                  to={`/news/detail/${featuredNews.id}`}
                  className="group relative block h-full min-h-[320px] cursor-pointer overflow-hidden rounded-lg bg-primary-900 md:min-h-[380px]"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20still%20of%20a%20dimly%20lit%20old%20cinema%20screening%20room%20with%20an%20audience%20silhouette%20and%20a%20warm%20orange%20projector%20light%20cutting%20through%20deep%20navy%20blue%20darkness%2C%20atmospheric%20filmic%20mood%2C%20no%20text&width=1200&height=750&seq=news-headline-01&orientation=landscape"
                    alt="头条资讯配图"
                    title="影视资讯 头条"
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/10" />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-background-100/85">
                      <span className="rounded bg-accent-500 px-2 py-0.5 font-semibold text-background-50">头条</span>
                      <span className="whitespace-nowrap">{featuredNews.tag}</span>
                      <span className="text-background-300">|</span>
                      <span className="whitespace-nowrap">{featuredNews.date}</span>
                    </div>
                    <h3 className="mt-3 line-clamp-2 font-heading text-lg font-bold leading-snug text-background-50 md:text-2xl">
                      {featuredNews.title}
                    </h3>
                    <p className="mt-2 hidden line-clamp-2 max-w-2xl text-sm leading-relaxed text-background-100/80 sm:block">
                      {featuredNews.summary}
                    </p>
                  </div>
                </Link>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
                  {sideNews.map((article) => (
                    <NewsCard key={article.id} article={article} size="compact" />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="mt-8 rounded-lg border border-background-200/80 bg-background-50 p-8 text-center text-sm text-foreground-600">
              暂无新闻资讯
            </p>
          )}
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-6 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">关于「女友的妈妈」影视专区</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              我们是一个专注影视剧情与行业资讯的内容门户。这里既有电影长片的完整剧情脉络，也有电视剧的分集信息与角色关系梳理，配合幕后特辑与深度解读，帮助你在观看之前与观看之后，都能更完整地理解一部作品。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: "ri-film-line", value: "12+", label: "在列影视作品" },
                { icon: "ri-article-line", value: "12 篇", label: "资讯与解读" },
                { icon: "ri-eye-line", value: "350 万+", label: "累计浏览人次" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/80 bg-background-100 p-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${stat.icon} text-base leading-none`} />
                  </span>
                  <p className="mt-3 font-heading text-lg font-bold text-foreground-950">{stat.value}</p>
                  <p className="mt-0.5 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="h-full rounded-lg border border-background-200/80 bg-secondary-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">本站说明</h3>
              <ul className="mt-3 space-y-3">
                {[
                  "影视资料、剧情介绍与资讯均为演示数据，仅用于信息查阅。",
                  "网站不提供影片播放、下载或任何付费服务。",
                  "推荐与相关内容依据分类、标签做简单关联展示。",
                  "搜索为前端演示搜索，支持影视名称与资讯标题关键词。",
                ].map((text) => (
                  <li key={text} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                      <i className="ri-check-line text-sm leading-none" />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-secondary-600"
              >
                了解更多
                <i className="ri-arrow-right-line text-sm leading-none" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}